import { useCallback, useEffect } from 'react';
import type { SerializeFrom } from '@remix-run/node';
import { useFetcher } from '@remix-run/react';
import { useLatest } from 'ahooks';
import {
    UPDATE_CONVERSATION_API,
    type action,
} from '~/routes/remix-api.chat.update-conversation';
import {
    UPDATE_CONVERSATION_THUMBNAIL_API,
    type action as updateThumbnailAction,
} from '~/routes/remix-api.chat.update-thumbnail';
import {
    LEAVE_CONVERSATION_API,
    type action as leaveAction,
} from '~/routes/remix-api.chat.leave-conversation';
import {
    ADD_PARTICIPANTS_API,
    type action as addParticipantsAction,
} from '~/routes/remix-api.chat.add-participants';
import {
    REMOVE_PARTICIPANT_API,
    type action as removeParticipantAction,
} from '~/routes/remix-api.chat.remove-participant';
import {
    ARCHIVE_CONVERSATION_API,
    type action as archiveAction,
} from '~/routes/remix-api.chat.archive';
import { useConversationCache } from './use-conversation-cache';

type UpdateConversationResponse = NonNullable<
    SerializeFrom<typeof action>
>['conversation'];

/**
 * Hook to update the settings of a (direct) conversation.
 */
export function useUpdateConversation({
    conversationID,
    onUpdateSuccess,
    onUpdateThumbnailSuccess,
    onLeaveSuccess,
    onAddSuccess,
    onRemoveSuccess,
    onArchiveSuccess,
}: {
    conversationID: number;
    /**
     * Callback when the conversation settings are updated.
     */
    onUpdateSuccess?: (conversation: UpdateConversationResponse) => void;
    /**
     * Callback when the conversation thumbnail is updated.
     */
    onUpdateThumbnailSuccess?: () => void;
    /**
     * Callback when the user leaves the conversation.
     */
    onLeaveSuccess?: () => void;
    /**
     * Callback when participants are added to the conversation.
     */
    onAddSuccess?: () => void;
    /**
     * Callback when a participant is removed from the conversation.
     */
    onRemoveSuccess?: () => void;
    /**
     * Callback when the conversation is archived/unarchived.
     */
    onArchiveSuccess?: (isArchived: boolean) => void;
}) {
    const { updateCachedConversation, invalidateCachedConversation } =
        useConversationCache();
    const updateFetcher = useFetcher<typeof action>();
    const updateThumbnailFetcher = useFetcher<typeof updateThumbnailAction>();
    const leaveFetcher = useFetcher<typeof leaveAction>();
    const addFetcher = useFetcher<typeof addParticipantsAction>();
    const removeFetcher = useFetcher<typeof removeParticipantAction>();
    const archiveFetcher = useFetcher<typeof archiveAction>();
    const latestOnUpdateSuccess = useLatest(onUpdateSuccess);
    const latestOnUpdateThumbnailSuccess = useLatest(onUpdateThumbnailSuccess);
    const latestOnLeaveSuccess = useLatest(onLeaveSuccess);
    const latestOnAddSuccess = useLatest(onAddSuccess);
    const latestOnRemoveSuccess = useLatest(onRemoveSuccess);
    const latestOnArchiveSuccess = useLatest(onArchiveSuccess);

    const updateConversation = useCallback(
        ({
            name,
            buildHubID,
        }: {
            name?: string | null;
            buildHubID?: number | null;
        }) => {
            if (conversationID <= 0) return;
            updateFetcher.submit(
                {
                    conversationID,
                    name: name ?? null,
                    buildHubID: buildHubID ?? null,
                },
                {
                    action: UPDATE_CONVERSATION_API,
                    method: 'POST',
                    encType: 'application/json',
                }
            );
        },
        [updateFetcher, conversationID]
    );

    const updateConversationThumbnail = useCallback(
        (thumbnail: File | null | undefined) => {
            if (conversationID <= 0) return;
            const formData = new FormData();
            formData.append('conversationID', String(conversationID));
            if (thumbnail) {
                formData.append('thumbnail', thumbnail);
            }
            updateThumbnailFetcher.submit(formData, {
                action: UPDATE_CONVERSATION_THUMBNAIL_API,
                method: 'POST',
                encType: 'multipart/form-data',
            });
        },
        [updateThumbnailFetcher, conversationID]
    );

    const leaveConversation = useCallback(() => {
        if (conversationID <= 0) return;
        leaveFetcher.submit(
            { conversationID },
            {
                action: LEAVE_CONVERSATION_API,
                method: 'POST',
                encType: 'application/json',
            }
        );
    }, [leaveFetcher, conversationID]);

    const addParticipants = useCallback(
        (memberIDs: number[]) => {
            if (conversationID <= 0) return;
            addFetcher.submit(
                { conversationID, memberIDs },
                {
                    action: ADD_PARTICIPANTS_API,
                    method: 'POST',
                    encType: 'application/json',
                }
            );
        },
        [addFetcher, conversationID]
    );

    const removeParticipant = useCallback(
        (participantID: string | number) => {
            if (conversationID <= 0) return;
            removeFetcher.submit(
                { conversationID, participantID },
                {
                    action: REMOVE_PARTICIPANT_API,
                    method: 'POST',
                    encType: 'application/json',
                }
            );
        },
        [removeFetcher, conversationID]
    );

    const archiveConversation = useCallback(
        (isArchived: boolean) => {
            if (conversationID <= 0) return;
            archiveFetcher.submit(
                { conversationID, isArchived },
                {
                    action: ARCHIVE_CONVERSATION_API,
                    method: 'POST',
                    encType: 'application/json',
                }
            );
        },
        [archiveFetcher, conversationID]
    );

    // Run callback and update cached conversation when update conversation mutation succeeds.
    useEffect(() => {
        if (updateFetcher.data && updateFetcher.state !== 'submitting') {
            const updatedConversation = updateFetcher.data.conversation;
            const systemMessages = updateFetcher.data.systemMessages;
            updateCachedConversation(Number(updatedConversation.ID), {
                Name: updatedConversation.Name ?? null,
                BuildHub: updatedConversation.BuildHub ?? null,
                Messages: systemMessages,
            });
            latestOnUpdateSuccess.current?.(updatedConversation);
        }
    }, [updateFetcher, latestOnUpdateSuccess, updateCachedConversation]);

    // Run callback and update cached conversation when update conversation thumbnail mutation succeeds.
    useEffect(() => {
        if (
            updateThumbnailFetcher.data &&
            updateThumbnailFetcher.state !== 'submitting'
        ) {
            const updatedConversation =
                updateThumbnailFetcher.data.conversation;
            const systemMessages = updateThumbnailFetcher.data.systemMessages;
            updateCachedConversation(Number(updatedConversation.ID), {
                Thumbnail: updatedConversation.Thumbnail ?? null,
                Messages: systemMessages,
            });
            latestOnUpdateThumbnailSuccess.current?.();
        }
    }, [
        updateThumbnailFetcher,
        latestOnUpdateThumbnailSuccess,
        updateCachedConversation,
    ]);

    // Run callback and invalidate cached conversation when leave conversation mutation succeeds.
    useEffect(() => {
        if (leaveFetcher.data && leaveFetcher.state !== 'submitting') {
            const updatedConversation = leaveFetcher.data.conversation;
            // User should no longer see the conversation they left.
            invalidateCachedConversation(Number(updatedConversation.ID));
            latestOnLeaveSuccess.current?.();
        }
    }, [leaveFetcher, latestOnLeaveSuccess, invalidateCachedConversation]);

    // Run callback and update cached conversation when add participants mutation succeeds.
    useEffect(() => {
        if (addFetcher.data && addFetcher.state !== 'submitting') {
            const updatedConversation = addFetcher.data.conversation;
            const systemMessages = addFetcher.data.systemMessages;
            updateCachedConversation(Number(updatedConversation.ID), {
                Participants: updatedConversation.Participants,
                Messages: systemMessages,
            });
            latestOnAddSuccess.current?.();
        }
    }, [addFetcher, latestOnAddSuccess, updateCachedConversation]);

    // Run callback and update cached conversation when remove participant mutation succeeds.
    useEffect(() => {
        if (removeFetcher.data && removeFetcher.state !== 'submitting') {
            const updatedConversation = removeFetcher.data.conversation;
            const systemMessages = removeFetcher.data.systemMessages;
            updateCachedConversation(Number(updatedConversation.ID), {
                Participants: updatedConversation.Participants,
                Messages: systemMessages,
            });
            latestOnRemoveSuccess.current?.();
        }
    }, [removeFetcher, latestOnRemoveSuccess, updateCachedConversation]);

    // Run callback and update cached conversation when archive conversation mutation succeeds.
    useEffect(() => {
        if (archiveFetcher.data && archiveFetcher.state === 'loading') {
            const updatedConversation = archiveFetcher.data.conversation;
            updateCachedConversation(Number(updatedConversation.ID), {
                IsArchived: updatedConversation.IsArchived,
            });
            latestOnArchiveSuccess.current?.(updatedConversation.IsArchived);
        }
    }, [archiveFetcher, latestOnArchiveSuccess, updateCachedConversation]);

    return {
        /**
         * One of the update fetchers are submitting.
         */
        isSubmitting:
            updateFetcher.state === 'submitting' ||
            updateThumbnailFetcher.state === 'submitting' ||
            leaveFetcher.state === 'submitting' ||
            addFetcher.state === 'submitting' ||
            removeFetcher.state === 'submitting' ||
            archiveFetcher.state === 'submitting',
        /**
         * Update the conversation settings. Can update the following fields:
         * - Name (title).
         * - BuildHubID. Set to `0` to unlink the project.
         */
        updateConversation,
        /**
         * Update the conversation thumbnail. Only works for direct conversations.
         */
        updateConversationThumbnail,
        /**
         * Leave the conversation. Only works for group chats with more than 2 participants.
         */
        leaveConversation,
        /**
         * Add participants to the conversation. Only works for group chats.
         */
        addParticipants,
        /**
         * Remove a participant from the conversation. Only works for group chats with more than 2 participants.
         */
        removeParticipant,
        /**
         * Archive/unarchive the conversation for the current user.
         */
        archiveConversation,
    };
}
